import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import FilterDropdown from '../../components/common/FilterDropdown';
import { GenericView, SearchInput, ActionButtons } from '../../components/common/table';
import SalesTargetForm from './components/SalesTargetForm';
import ViewSalesTarget from './components/ViewSalesTarget';
import Alert from '../../components/Alert/Alert';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import { useAuth } from '../../contexts/AuthProvider';
import {
  getSalesTargets,
  getSalesTarget,
  deleteSalesTarget,
  updateSalesTarget,
  createSalesTarget
} from './api/salesTargetApi';

const SalesTarget = () => {
  const navigate = useNavigate();
  const { user, can } = useAuth();
  const canSalesTarget = (capabilityKey) => can(capabilityKey, 'sales-target');

  const canViewPage = canSalesTarget('view_global') || canSalesTarget('view_department');

  const [searchQuery, setSearchQuery] = useState('');
  const [filterData, setFilterData] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedSalesTarget, setSelectedSalesTarget] = useState(null);
  const [salesTargets, setSalesTargets] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [alert, setAlert] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const showAlert = (type, message) => {
    setAlert({ type, message });
    setTimeout(() => setAlert(null), 5000); // Auto hide after 5 seconds
  };

  useEffect(() => {
    if (canViewPage) loadSalesTargets();
  }, [canViewPage, user?.id, user?.departmentId]);

  const loadSalesTargets = async () => {
    if (!canViewPage) return;
    setIsLoading(true);
    try {
      const params = {};
      if (!canSalesTarget('view_global') && canSalesTarget('view_department') && user?.departmentId) {
        params.viewDepartmentId = user.departmentId;
      }
      const response = await getSalesTargets(params);
      const data = Array.isArray(response) ? response : response.data || [];
      setSalesTargets(data);
    } catch (error) {
      console.error('Error loading sales targets:', error);
      showAlert('danger', 'Failed to load sales targets.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (name, value) => {
    setFilterData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFilterReset = () => {
    setFilterData({
      dateFrom: '',
      dateTo: '',
      year: ''
    });
  };

  const handleFilterApply = () => {
    // Add your filter logic here
    loadSalesTargets();
  };

  const formatCurrency = (value) => {
    if (!value) return '0';
    const numValue = typeof value === 'string' ? parseFloat(value.replace(/[^\d]/g, '')) : parseFloat(value);
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(numValue);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (error) {
      return dateString;
    }
  };

  const columns = [
    { 
      key: 'year', 
      label: 'Year',
      sortable: true
    },
    { 
      key: 'departmentName', 
      label: 'Department',
      sortable: true,
      render: (value, row) => row.departmentName || row.department_name || '-'
    },
    { 
      key: 'amount', 
      label: 'Department Target (IDR)',
      sortable: true,
      render: (value) => formatCurrency(value)
    },
    { 
      key: 'allocatedAmount', 
      label: 'Allocated to Sales (IDR)',
      sortable: true,
      render: (value, row) => {
        const allocated = row.allocatedAmount || row.allocated_amount;
        return formatCurrency(allocated);
      }
    },
    { 
      key: 'createdAt', 
      label: 'Create Date',
      sortable: true,
      type: 'date',
      render: (value, row) => formatDate(value || row.created_at)
    },
    { 
      key: 'updatedAt', 
      label: 'Update Date',
      sortable: true,
      type: 'date',
      render: (value, row) => formatDate(value || row.updated_at)
    },
    { 
      key: 'createdBy', 
      label: 'Created By',
      sortable: true,
      render: (value, row) => {
        if (row.createdByUser) {
          const firstName = row.createdByUser.firstName || '';
          const lastName = row.createdByUser.lastName || '';
          const fullName = `${firstName} ${lastName}`.trim();
          if (fullName) {
            return fullName;
          }
          // Fallback to username
          return row.createdByUser.username || '-';
        }
        return '-';
      }
    },
    { 
      key: 'updatedBy', 
      label: 'Updated By',
      sortable: true,
      render: (value, row) => {
        if (row.updatedByUser) {
          const firstName = row.updatedByUser.firstName || '';
          const lastName = row.updatedByUser.lastName || '';
          const fullName = `${firstName} ${lastName}`.trim();
          if (fullName) {
            return fullName;
          }
          // Fallback to username
          return row.updatedByUser.username || '-';
        }
        return '-';
      }
    }
  ];

  const handleFormSubmit = async (payload) => {
    try {
      if (selectedSalesTarget) {
        await updateSalesTarget(selectedSalesTarget.id, payload);
        showAlert('success', 'Sales target updated successfully!');
      } else {
        await createSalesTarget(payload);
        showAlert('success', 'Sales target created successfully!');
      }
      await loadSalesTargets();
      setIsModalOpen(false);
      setSelectedSalesTarget(null);
    } catch (error) {
      console.error('Error saving sales target:', error);
      showAlert('danger', 'Failed to save sales target. Please check your input.');
    }
  };

  const handleEdit = async (row) => {
    if (!canSalesTarget('edit')) {
      showAlert('danger', 'You do not have permission to edit sales targets.');
      return;
    }
    try {
      // Fetch full details (including per-sales targets) by ID
      const full = await getSalesTarget(row.id);
      const data = full && full.data ? full.data : full;
      setSelectedSalesTarget(data);
      setIsModalOpen(true);
    } catch (error) {
      console.error('Error loading sales target details:', error);
      showAlert('danger', 'Failed to load sales target details.');
    }
  };

  const handleDelete = async (row) => {
    if (!canSalesTarget('delete')) {
      showAlert('danger', 'You do not have permission to delete sales targets.');
      return;
    }
    setItemToDelete(row);
    setShowDeleteModal(true);
  };

  const handleView = (row) => {
    setSelectedSalesTarget(row);
    setIsViewModalOpen(true);
  };

  if (!canViewPage) {
    return (
      <div className="container-xl">
        {alert && (
          <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />
        )}
        <div className="page-body">
          <div className="card">
            <div className="card-body text-center py-5">
              <p className="text-muted mb-0">You don&apos;t have permission to view sales targets. Please contact your administrator to grant view (department or global) permission.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-xl">
      {/* Alert Component */}
      {alert && (
        <Alert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}

      <div className="row g-2 align-items-center">
        <div className="col">
          <h3 className="page-title m-0">Sales Target Management</h3>
        </div>
        <div className="col-auto ms-auto d-print-none">
          <div className="btn-list">
            {canSalesTarget('create') && (
              <button 
                className="btn btn-primary"
                onClick={() => setIsModalOpen(true)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-plus" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                  <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                  <path d="M12 5l0 14"></path>
                  <path d="M5 12l14 0"></path>
                </svg>
                Create
              </button>
            )}
          </div>
        </div>
      </div>

      <SalesTargetForm
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedSalesTarget(null);
        }}
        onSubmit={handleFormSubmit}
        initialData={selectedSalesTarget}
      />

      <ViewSalesTarget
        isOpen={isViewModalOpen}
        onClose={() => {
          setIsViewModalOpen(false);
          setSelectedSalesTarget(null);
        }}
        data={selectedSalesTarget}
      />

      {/* Confirmation Modal for Delete */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setItemToDelete(null);
        }}
        onConfirm={async () => {
          if (!itemToDelete?.id) return;
          setIsLoading(true);
          try {
            await deleteSalesTarget(itemToDelete.id);
            showAlert('success', 'Sales target deleted successfully!');
            await loadSalesTargets();
          } catch (error) {
            console.error('Error deleting sales target:', error);
            showAlert('danger', 'Failed to delete sales target.');
          } finally {
            setIsLoading(false);
          }
          setShowDeleteModal(false);
          setItemToDelete(null);
        }}
        title="Delete Sales Target"
        message={`Are you sure you want to delete the sales target for year ${itemToDelete?.year}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
      />

      <div className="page-body">
        <div className="container-xl">
          <div className="col">
            <div className="card">
              <div className="card-header">
                <ul className="nav nav-tabs card-header-tabs" data-bs-toggle="tabs" role="tablist">
                  <li className="nav-item-tab" role="presentation">
                    <a href="#tabs-list-data" className="nav-link active" data-bs-toggle="tab" aria-selected="true" role="tab">List Data</a>
                  </li>
                </ul>
              </div>
              <div className="card-body">
                <div className="tab-content">
                  <div className="tab-pane active show" id="tabs-list-data" role="tabpanel">
                    <GenericView
                      data={salesTargets}
                      columns={columns}
                      onEdit={canSalesTarget('edit') ? handleEdit : undefined}
                      onDelete={canSalesTarget('delete') ? handleDelete : undefined}
                      onView={handleView}
                      isLoading={isLoading}
                      filterComponent={
                        <FilterDropdown
                          filterData={filterData}
                          onFilterChange={handleFilterChange}
                          onReset={handleFilterReset}
                          onApply={handleFilterApply}
                        />
                      }
                      searchComponent={
                        <SearchInput
                          value={searchQuery}
                          onChange={setSearchQuery}
                          placeholder="Search sales targets..."
                        />
                      }
                      actionButtons={
                        <ActionButtons
                          onExportExcel={() => {
                            showAlert('info', 'Export to Excel functionality coming soon');
                          }}
                          onExportPDF={() => {
                            showAlert('info', 'Export to PDF functionality coming soon');
                          }}
                        />
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesTarget;

